import { api } from "./axios";
import { ComplaintStatus, Priority } from "@/components/shared/StatusBadge";

interface Envelope<T> {
  success: true;
  data: T;
  meta?: { page: number; limit: number; total: number };
}

export interface Customer {
  id: string;
  customerCode: string;
  fullName: string;
  phone: string;
  email: string | null;
  address: string | null;
  company: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerComplaint {
  id: string;
  complaintNumber: string;
  productName: string;
  status: ComplaintStatus;
  priority: Priority;
  createdAt: string;
  expectedCompletionDate: string | null;
  assignedEmployee: { id: string; fullName: string } | null;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
}

export interface CustomerInput {
  fullName: string;
  phone: string;
  email?: string;
  address?: string;
  company?: string;
  notes?: string;
}

export const customerApi = {
  async list(params: { page: number; limit: number; search?: string }): Promise<PagedResult<Customer>> {
    const res = await api.get<Envelope<Customer[]>>("/customers", { params });
    return { items: res.data.data, page: res.data.meta!.page, limit: res.data.meta!.limit, total: res.data.meta!.total };
  },

  async getById(id: string): Promise<Customer> {
    const res = await api.get<Envelope<Customer>>(`/customers/${id}`);
    return res.data.data;
  },

  async getComplaints(id: string, params: { page: number; limit: number }): Promise<PagedResult<CustomerComplaint>> {
    const res = await api.get<Envelope<CustomerComplaint[]>>(`/customers/${id}/complaints`, { params });
    return { items: res.data.data, page: res.data.meta!.page, limit: res.data.meta!.limit, total: res.data.meta!.total };
  },

  async create(input: CustomerInput): Promise<Customer> {
    const res = await api.post<Envelope<Customer>>("/customers", input);
    return res.data.data;
  },

  async update(id: string, input: Partial<CustomerInput>): Promise<Customer> {
    const res = await api.put<Envelope<Customer>>(`/customers/${id}`, input);
    return res.data.data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/customers/${id}`);
  },
};
